import { unzipSync } from 'fflate';
import { MAX_ICON_ZIP_BYTES, MAX_ICONS_PER_RUN } from './constants';

/**
 * Unpacks a ZIP of icons in the browser. [AVP-891]
 *
 * The archive is never uploaded. At 100–1000 icons there is nothing to gain
 * from a server-side ZIP endpoint — it would still need a client-side unzip to
 * get under nginx's 20 MB body cap — and it would add zip-bomb and
 * path-traversal handling to a Java service for no benefit. Here the entries
 * become ordinary `File`s and go through exactly the same batch path as files
 * picked from a folder.
 *
 * fflate resolves to `esm/browser.js` under webpack 4 (its `exports` map is
 * ignored, `module` wins), which is plain ES5 — CRA does not transpile
 * node_modules, so that matters.
 */

const MIME_BY_EXTENSION: Record<string, string> = {
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  webp: 'image/webp',
};

const basenameOf = (path: string): string => path.split('/').pop() ?? path;

export const isZipNoise = (path: string): boolean => {
  if (path.endsWith('/')) {
    return true;
  }
  if (path.startsWith('__MACOSX/') || path.includes('/__MACOSX/')) {
    return true;
  }
  const base = basenameOf(path);
  return base === '' || base.startsWith('.') || base === 'Thumbs.db' || base === 'desktop.ini';
};

export const mimeOf = (name: string): string => {
  const extension = name.includes('.') ? name.split('.').pop()?.toLowerCase() ?? '' : '';
  return MIME_BY_EXTENSION[extension] ?? '';
};

const readArrayBuffer = (blob: Blob): Promise<ArrayBuffer> => (
  new Promise((resolve, reject) => {
    // FileReader rather than Blob.arrayBuffer(): jsdom 16.7 has no .arrayBuffer
    const reader = new FileReader();
    reader.onload = (): void => resolve(reader.result as ArrayBuffer);
    reader.onerror = (): void => reject(reader.error ?? new Error('Could not read the archive.'));
    reader.readAsArrayBuffer(blob);
  })
);

/**
 * @throws Error when the archive is too big or cannot be read
 */
export const readIconZip = async (zip: Blob): Promise<File[]> => {
  if (zip.size > MAX_ICON_ZIP_BYTES) {
    throw new Error(
      `The archive is larger than ${Math.round(MAX_ICON_ZIP_BYTES / (1024 * 1024))} MB.`,
    );
  }

  const buffer = await readArrayBuffer(zip);

  // unzipSync blocks, so hand the browser a frame to paint the spinner first
  await new Promise((resolve) => { setTimeout(resolve, 0); });

  let entries: Record<string, Uint8Array>;
  try {
    // Type is deliberately NOT filtered here. An .svg dropped silently by the
    // archive reader but reported with a reason by the folder picker is the
    // same archive telling the user two different stories; selectIconFiles
    // owns that judgement for both paths.
    entries = unzipSync(new Uint8Array(buffer), {
      filter: (file) => !isZipNoise(file.name),
    });
  } catch {
    throw new Error('That does not look like a readable ZIP archive.');
  }

  const names = Object.keys(entries).sort((left, right) => left.localeCompare(right));
  if (names.length === 0) {
    throw new Error('The archive has no files in it.');
  }
  if (names.length > MAX_ICONS_PER_RUN) {
    throw new Error(
      `The archive holds ${names.length} icons; the limit is ${MAX_ICONS_PER_RUN} per run.`,
    );
  }

  // The full entry path is kept as the file name. stemOf() takes the basename
  // anyway, so the code still resolves — but when two folders hold the same
  // stem, the rejection can say "a/SKU1.png already covers this code" instead
  // of naming the rejected file as its own winner.
  return names.map((name) => new File(
    [entries[name]],
    name,
    { type: mimeOf(name) },
  ));
};

export default readIconZip;
